 import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, collection, query, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { AudioRecorder } from '../components/AudioRecorder';
import { ArrowLeftIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export const QuestionPage = () => {
  console.log('🔧 QUESTION PAGE LOADED - This is QuestionPage.js component!');
  
  const { storyId, questionId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [story, setStory] = useState(null);
  const [question, setQuestion] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionAnswers, setQuestionAnswers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Collaborative story detection
  const [isCollaborativeStory, setIsCollaborativeStory] = useState(false);
  const [collaborativeAuthors, setCollaborativeAuthors] = useState([]);
  const [currentAuthorIndex, setCurrentAuthorIndex] = useState(0);
  const [hasMultipleAuthors, setHasMultipleAuthors] = useState(false);

  useEffect(() => {
    if (!storyId || !questionId || !currentUser) return;

    // Listen to story changes
    const storyRef = doc(db, 'stories', storyId);
    const unsubscribeStory = onSnapshot(storyRef, (doc) => {
      if (doc.exists()) {
        const storyData = { id: doc.id, ...doc.data() };
        setStory(storyData);
        
        // Collaborative story detection logic
        console.log('🎯 COLLABORATIVE DETECTION IN QUESTIONPAGE:');
        console.log('Story data:', storyData);
        
        const collaborators = storyData.collaborators || [];
        const isCollab = storyData.isCollaborative === true || collaborators.length >= 1;
        
        console.log('Collaborators:', collaborators);
        console.log('IsCollaborative flag:', storyData.isCollaborative);
        console.log('Detected as collaborative:', isCollab);
        
        setIsCollaborativeStory(isCollab);
        setCollaborativeAuthors(collaborators);
        setHasMultipleAuthors(collaborators.length >= 1); // Changed from > 1 to >= 1
        
        // Determine current author index
        const currentAuthorIdx = collaborators.findIndex(author => author.uid === currentUser.uid);
        setCurrentAuthorIndex(currentAuthorIdx >= 0 ? currentAuthorIdx : 0);
        
        console.log('Current author index:', currentAuthorIdx);
        console.log('Has multiple authors:', collaborators.length > 1);
      }
      setLoading(false);
    });

    // Listen to specific question changes
    const questionRef = doc(db, 'stories', storyId, 'questions', questionId);
    const unsubscribeQuestion = onSnapshot(questionRef, (doc) => {
      if (doc.exists()) {
        setQuestion({ id: doc.id, ...doc.data() });
      }
    });

    // Listen to all questions for navigation
    const questionsRef = collection(db, 'stories', storyId, 'questions');
    const q = query(questionsRef, orderBy('order'));
    const unsubscribeQuestions = onSnapshot(q, (snapshot) => {
      const questionList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionList);
    });

    // Listen to answers for this question
    const answersRef = collection(db, 'stories', storyId, 'questions', questionId, 'answers');
    const answersQuery = query(answersRef, orderBy('createdAt'));
    const unsubscribeAnswers = onSnapshot(answersQuery, (snapshot) => {
      const answerList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestionAnswers(answerList);
    });

    return () => {
      unsubscribeStory();
      unsubscribeQuestion();
      unsubscribeQuestions();
      unsubscribeAnswers();
    };
  }, [storyId, questionId, currentUser]);

  const handleAnswerComplete = async (answerData) => {
    // The answer is automatically saved by the AudioRecorder
    // We just need to refresh our local state, which happens via the snapshot listener
    console.log('Answer completed:', answerData);
  };

  const handleBackToQuestions = () => {
    navigate(`/story/${storyId}/questions`);
  };

  const getQuestionNumber = () => {
    if (!question || !questions.length) return null;
    const index = questions.findIndex(q => q.id === question.id);
    return index >= 0 ? index + 1 : null;
  };

  const getNavigationInfo = () => {
    if (!question || !questions.length) return { prev: null, next: null };
    
    const currentIndex = questions.findIndex(q => q.id === question.id);
    const prev = currentIndex > 0 ? questions[currentIndex - 1] : null;
    const next = currentIndex < questions.length - 1 ? questions[currentIndex + 1] : null;
    
    return { prev, next };
  };

  const handleNavigateToQuestion = (targetQuestion) => {
    navigate(`/story/${storyId}/question/${targetQuestion.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!story || !question) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-medium text-gray-900">Question not found</h2>
        <button
          onClick={handleBackToQuestions}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          Back to Questions
        </button>
      </div>
    );
  }

  const questionNumber = getQuestionNumber();
  const { prev, next } = getNavigationInfo();

  return (
    <div className="w-full md:max-w-2xl md:mx-auto px-2 md:px-4 py-4 md:py-6">
      {/* Header with Back Button */}
      <div className="flex items-center mb-6">
        <button
          onClick={handleBackToQuestions}
          className="flex items-center text-blue-600 hover:text-blue-800 transition-colors duration-200"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Questions
        </button>
      </div>

      {/* Story Title */}
      <div className="text-center mb-8">
        {isCollaborativeStory && (
          <div className="bg-orange-100 border border-orange-300 rounded-lg p-4 mb-4">
            <h2 className="text-lg font-bold text-orange-800">
              🎯 COLLABORATIVE STORY DETECTED IN QUESTIONPAGE!
            </h2>
            <div className="text-sm text-orange-700 mt-2">
              <p>Authors: {collaborativeAuthors.length} | HasMultiple: {hasMultipleAuthors.toString()}</p>
              <p>Current User: {collaborativeAuthors[currentAuthorIndex]?.displayName || 'Unknown'}</p>
            </div>
          </div>
        )}
        <h1 className="text-2xl font-bold text-red-600">🔧 TESTING: Individual Question View</h1>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{story.title}</h1>
        <div className="flex items-center justify-center space-x-2">
          {question.hasAnswer ? (
            <CheckCircleIcon className="h-5 w-5 text-green-500" />
          ) : (
            <CheckCircleIcon className="h-5 w-5 text-gray-400" />
          )}
          <span className="text-lg font-semibold text-gray-700">
            Question {questionNumber} of {questions.length}
          </span>
        </div>
      </div>

      {/* Question Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 mb-6 md:mb-8">
        <div className="text-center mb-4 md:mb-6">
          {question.hasAnswer && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 mb-4">
              ✓ Answered
            </span>
          )}
          <h2 className="text-xl font-semibold text-gray-900 leading-relaxed">
            {question.text}
          </h2>
        </div>

        {/* Recording Interface */}
        {isCollaborativeStory && hasMultipleAuthors ? (
          <div className="space-y-4 md:space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 mb-4 md:mb-6">
              <h3 className="text-base md:text-lg font-semibold text-blue-800 mb-2">
                🎤 Collaborative Story - Dual Author Recording
              </h3>
              <p className="text-blue-700">
                This question will be answered by both authors. Each author can record their own perspective.
              </p>
            </div>
            
            {collaborativeAuthors.map((author, index) => {
              const isCurrentUser = author.uid === currentUser.uid;
              const authorAnswers = questionAnswers.filter(answer => answer.authorId === author.uid);
              const hasAnswered = authorAnswers.length > 0;
              
              return (
                <div 
                  key={author.uid} 
                  className={`border rounded-lg p-3 md:p-4 ${
                    isCurrentUser 
                      ? 'border-blue-300 bg-blue-50' 
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3 md:mb-4">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        isCurrentUser ? 'bg-blue-500' : 'bg-gray-400'
                      }`}></div>
                      <h4 className="font-semibold text-gray-900">
                        {author.displayName || author.email || 'Author'}
                        {isCurrentUser && ' (You)'}
                      </h4>
                    </div>
                    {hasAnswered && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ Recorded
                      </span>
                    )}
                  </div>
                  
                  {isCurrentUser ? (
                    <AudioRecorder
                      storyId={storyId}
                      questionId={questionId}
                      question={question}
                      existingAnswers={authorAnswers}
                      onAnswerComplete={handleAnswerComplete}
                      useSteppedInterface={true}
                      collaborativeMode={true}
                      authorId={author.uid}
                      authorName={author.displayName || author.email}
                    />
                  ) : (
                    <div className="text-center py-8 text-gray-600">
                      {hasAnswered ? (
                        <div>
                          <CheckCircleIcon className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <p>This author has recorded their answer</p>
                        </div>
                      ) : (
                        <div>
                          <CheckCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                          <p>Waiting for this author to record their answer</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <AudioRecorder
            storyId={storyId}
            questionId={questionId}
            question={question}
            existingAnswers={questionAnswers}
            onAnswerComplete={handleAnswerComplete}
            useSteppedInterface={true}
          />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center">
        {prev ? (
          <button
            onClick={() => handleNavigateToQuestion(prev)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Previous
          </button>
        ) : (
          <div></div>
        )}

        {next ? (
          <button
            onClick={() => handleNavigateToQuestion(next)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            Next
            <ArrowLeftIcon className="h-4 w-4 ml-2 rotate-180" />
          </button>
        ) : (
          <button
            onClick={handleBackToQuestions}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            All Done! View All Questions
          </button>
        )}
      </div>

      {/* Progress Indicator */}
      <div className="mt-6 md:mt-8 bg-gray-50 rounded-lg p-3 md:p-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-900">Story Progress</span>
          <span className="text-sm text-gray-600">
            {questions.filter(q => q.hasAnswer).length} / {questions.length} answered
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
            style={{ 
              width: `${questions.length > 0 ? (questions.filter(q => q.hasAnswer).length / questions.length) * 100 : 0}%` 
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};
